import { ref, computed } from 'vue'
import { ErrorData } from '@/js/utils/validation'
import axios from 'axios'

type LoadingError = {
    method: string
    message: string
    errorData?: ErrorData
    status?: number
}

export function useLoading() {
    const loadingMethods = ref<Set<string>>(new Set())
    const errors = ref<LoadingError[]>([])

    const isLoading = computed(() => loadingMethods.value.size > 0)

    const hasError = (): boolean => errors.value.length > 0

    const getError = (methodName: string): LoadingError | undefined =>
        errors.value.find((e) => e.method === methodName)

    const clearError = (methodName: string): void => {
        errors.value = errors.value.filter((e) => e.method !== methodName)
    }
    const isMethodLoading = (methodName: string): boolean => {
        return loadingMethods.value.has(methodName)
    }

    const setLoading = async <T>(methodName: string, fn: () => Promise<T>): Promise<T> => {
        loadingMethods.value.add(methodName)
        clearError(methodName)

        try {
            return await fn()
        } catch (err: unknown) {
            if (axios.isAxiosError(err)) {
                errors.value.push({
                    method: methodName,
                    message:
                        err.response?.data?.description ||
                        err.response?.data?.['hydra:description'] ||
                        err.message ||
                        'Unbekannter Fehler',
                    errorData: err.response?.data,
                    status: err.response?.status
                })
            } else {
                errors.value.push({
                    method: methodName,
                    message: 'Unbekannter Fehler'
                })
            }

            throw err
        } finally {
            loadingMethods.value.delete(methodName)
        }
    }

    return {
        isLoading,
        isMethodLoading,
        loadingMethods,
        setLoading,
        errors,
        hasError,
        getError,
        clearError
    }
}
